import { JsonPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize, timeout } from 'rxjs';
import { HindsightSettingsService } from './hindsight-settings.service';
import { Budget, TagsMatch, QueryMode, RagResponse, QueryResult, QueryRequest } from './hindsight.models';
import { JkBaseComponent } from 'jk-service';

@Component({
  selector: 'app-hindsight-query',
  standalone: true,
  imports: [FormsModule, JsonPipe],
  templateUrl: './hindsight-query.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HindsightQueryComponent extends JkBaseComponent implements OnInit {
  override comType: string = 'hindsight-query';
  private readonly destroyRef = inject(DestroyRef);
  readonly settings = inject(HindsightSettingsService);
  get apiBaseUrl(): string { return this.settings.value().apiBaseUrl || this.login.host; }
  get mode(): QueryMode { return this.settings.value().mode; }
  get budget(): Budget { return this.settings.value().budget; }
  get maxTokens(): number { return this.settings.value().maxTokens; }
  get tags(): string { return this.settings.value().tags; }
  get tagsMatch(): TagsMatch { return this.settings.value().tagsMatch; }
  readonly busy = signal(false);
  readonly error = signal('');
  readonly details = signal<unknown>(null);
  readonly response = signal<QueryResult | null>(null);
  readonly completedMode = signal<QueryMode | null>(null);
  query: string = '';

  override ngOnInit(): void {
    super.ngOnInit();
  }

  private endpoint(action: string): string {
    const base = this.apiBaseUrl.trim().replace(/\/+$/, '');
    if (!/^https?:\/\//i.test(base) && !base.startsWith('/')) {
      throw new Error('請輸入完整 http(s) API 網址或以 / 開頭的代理路徑。');
    }
    return `${base.endsWith('/rag') ? base : `${base}/rag`}/${action}`;
  }

  search(): void {
    if (this.busy()) return;
    this.error.set('');
    this.details.set(null);
    this.response.set(null);
    this.completedMode.set(null);
    if (!this.query.trim()) {
      this.error.set('請輸入查詢內容。');
      return;
    }
    if (!Number.isInteger(this.maxTokens) || this.maxTokens < 1) {
      this.error.set('max_tokens 必須為正整數。');
      return;
    }
    const mode = this.mode;
    const tags = [...new Set(this.tags.split(/[,，\n]/).map(tag => tag.trim()).filter(Boolean))];
    const body: QueryRequest = {
      query: this.query.trim(), budget: this.budget, max_tokens: this.maxTokens,
      ...(tags.length ? { tags, tags_match: this.tagsMatch } : {}),
      ...(mode === 'reflect' ? { include: { facts: {} } } : {}),
    };
    this.request(mode, body);
  }

  private request(action: QueryMode, body: QueryRequest): void {
    this.error.set('');
    this.details.set(null);
    let url: string;
    try { url = this.endpoint(action); }
    catch (error: unknown) {
      this.error.set(error instanceof Error ? error.message : 'API 網址不正確。');
      return;
    }
    this.busy.set(true);
    const request$ = this.http.post<RagResponse>(url, body);
    request$.pipe(
      timeout(190000),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.busy.set(false)),
    ).subscribe({
      next: (result: RagResponse) => {
        if (result.result !== true) {
          this.error.set(`API 請求失敗（${result.statusCode ?? '未知狀態'}）`);
          this.details.set(result);
          return;
        }
        if (!result.obj || (action === 'recall' ? !Array.isArray(result.obj.results) : typeof result.obj.text !== 'string')) {
          this.error.set('API 回傳格式不符合 Hindsight 查詢規格。');
          this.details.set(result);
          return;
        }
        this.response.set(result.obj);
        this.completedMode.set(action);
      },
      error: (error: unknown) => {
        this.error.set(error instanceof HttpErrorResponse
          ? error.status === 0 ? '無法連線，請確認 API 網址、服務狀態及 CORS 設定。' : `API 請求失敗（HTTP ${error.status}）`
          : '請求逾時或發生錯誤，請稍後再試。');
        if (error instanceof HttpErrorResponse) this.details.set(error.error as unknown);
      },
    });
  }
}
